import { apiListPosts, apiGetPost, apiCreatePost as apiCreatePostRaw, apiAddComment as apiAddCommentRaw } from '../services/api';
import type { Post as UiPost, User, Author, Comment, CommentDTO } from '../types';

export type PostDTO = {
  id: string;
  authorId: string;
  author: any;
  content: string;
  imageUrl?: string | null;
  likes: string[];
  comments: CommentDTO[];
  tags: string[];
  createdAt: string;
  updatedAt: string;
};

export type FetchPostsPage = {
  items: UiPost[];
  nextCursor: string | null;
};

type AuthorDTO = { id?: string; displayName?: string | null; profileImage?: string | null };
const fallbackAuthor = (a?: AuthorDTO | null): User => ({
  id: a?.id ?? '',
  email: '',
  firstName: '',
  lastName: '',
  displayName: a?.displayName ?? 'Unknown Student',
  profilePicture: a?.profileImage ?? undefined,
  major: '',
  year: 0,
  hometown: '',
  interests: [],
  clubs: [],
  courses: [],
  createdAt: new Date(),
  updatedAt: new Date(),
});

const toAuthor = (a: any): Author => ({
  id: a?.id ?? '',
  displayName: a?.displayName ?? 'Unknown Student',
  profileImage: a?.profileImage ?? null,
});

const toComment = (c: CommentDTO): Comment => ({
  id: c.id,
  content: c.content,
  parentId: c.parentId ?? null,
  createdAt: new Date(c.createdAt),
  author: toAuthor((c as any).author),
  children: (c.children ?? []).map(toComment)
});

const toPost = (p: PostDTO): UiPost => ({
  ...p,
  author: fallbackAuthor(p.author),
  imageUrl: (p as any).imageUrl ?? null,
  likes: p.likes ?? [],
  tags: p.tags ?? [],
  createdAt: new Date(p.createdAt),
  updatedAt: new Date(p.updatedAt),
  comments: (p.comments ?? []).map(toComment),
});

export async function fetchPosts(opts?: { globalOnly?: boolean }): Promise<UiPost[]> {
  const page = await fetchPostsPage({ globalOnly: opts?.globalOnly });
  return page.items;
}

export async function fetchPostsPage(opts?: {
  globalOnly?: boolean;
  cursor?: string | null;
  take?: number;
}): Promise<FetchPostsPage> {
  const res = await apiListPosts(opts?.globalOnly, opts?.cursor, opts?.take ?? 12);
  const list = (res as any).data || [];
  return {
    items: (list as PostDTO[]).map(toPost),
    nextCursor: (res as any).nextCursor ?? null,
  };
}

export async function fetchPost(postId: string): Promise<UiPost> {
  const res = await apiGetPost(postId);
  return toPost((res as any).data as PostDTO);
}

export async function createPost(content: string, imageUrl?: string | null): Promise<UiPost> {
  const res = await apiCreatePostRaw(content, imageUrl ?? undefined);
  const created = (res as any).data as PostDTO;
  return toPost(created);
}

export async function addComment(postId: string, content: string, parentId?: string) {
  const idKey = (globalThis as any).crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);
  const res = await apiAddCommentRaw(postId, { content, parentId }, { idempotencyKey: idKey });
  const payload = (res as any).data ?? res; // support both wrapped and unwrapped
  return toComment(payload as CommentDTO);
}
